import * as Sentry from '@sentry/react';

import { HTTPError } from 'entities/Error';
import { ENV_TYPE, IS_LOCAL } from 'utils/envFeatures';

const SENTRY_VENDOR_TAG = import.meta.env.VITE_SENTRY_VENDOR;

export const initSentry = () => {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: ENV_TYPE,
    enabled: !IS_LOCAL,
    normalizeDepth: 6,
    tracesSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
  });

  if (SENTRY_VENDOR_TAG) {
    Sentry.setTag('vendor', SENTRY_VENDOR_TAG);
  }
};

export enum SentryErrorKind {
  api = 'api',
  normalization = 'normalization',
  authorization = 'authorization',
  other = 'other',
}

type ExtraContext = Record<string, unknown>;

const captureNonError = (payload: unknown, errorKind: SentryErrorKind, extra: ExtraContext) => {
  Sentry.captureException(new Error('Non-error payload captured'), {
    tags: { errorKind },
    extra: { ...extra, payload },
  });
};

export const logWithSentry = (error: unknown, errorKind: SentryErrorKind, extra: ExtraContext = {}) => {
  const captureContext = { tags: { errorKind }, extra };

  if (error instanceof Error) {
    Sentry.captureException(error, captureContext);

    return;
  }

  if (typeof error === 'string') {
    Sentry.captureException(new Error(error), captureContext);

    return;
  }

  captureNonError(error, errorKind, extra);
};

export const logApiErrWithSentry = (params: {
  endpoint: string;
  method: string;
  requestPayload: any;
  response: any;
  extraData?: any;
}) => {
  if (params.response?.cancelled) {
    return;
  }
  logWithSentry(new HTTPError(params.method, params.endpoint), SentryErrorKind.api, {
    requestPayload: params.requestPayload,
    response: params.response,
    extraData: params.extraData,
  });
};
